import { useAuthStore } from "./auth";
import dayjs from "dayjs";
import { getDatabase, onDisconnect, onValue, ref, remove, serverTimestamp, set } from "firebase/database";
import type { DatabaseReference } from "firebase/database";
import { useFirebase, useFirebaseFirestore } from "../composables/useFirebase";
import { useRunningMode, migrateRunMode } from "../composables/useRunningMode";
import { fetchUserRecord, setResolvedStoreId } from "../composables/useStoreIdentity";
import { ensureComputerIdentity, readComputerIdentity } from "../composables/useComputerIdentity";
import dataservice from "../services/dataservice";
import DataService from "../services/dataservice/dataservice.base";
import { setDoc, query, onSnapshot, where , doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import * as Sentry from "@sentry/vue";
import { getDevStoreOverride, isDevReadOnly, setDevRealStore } from "../utils/devStoreOverride";
import { getLiveSyncStatus } from "../utils/liveSyncStatus";
import { formatDuration, getPresenceDeviceInfo } from "../utils/presenceInfo";
import { betaOrigin, isBetaSite, writeHandoffCookie } from "../utils/authHandoff";
import packageData from "~/package.json";
import { BOOTSTRAP_STOP } from "./bootstrap";

// presence ของเครื่องนี้ (stores/{id}/online/{computerUuid}) เก็บนอก state
// เพราะเป็น handle ของ timer/listener ไม่ใช่ข้อมูลให้ UI
let presenceTimer: ReturnType<typeof setInterval> | null = null;
let presenceUnsubConnected: (() => void) | null = null;
let presenceRef: DatabaseReference | null = null;
// สร้าง payload heartbeat ของเครื่องนี้ (ตั้งใน trackCashierOnline) — clearOnlineNodes ใช้เขียนตัวเองกลับทันที
let presenceBuildPayload: (() => Record<string, any>) | null = null;

const CLEAR_ONLINE_TIMEOUT_MS = 3000;

// auth ,  restore, fetch, watch , ready
export const useShopStore = defineStore("shop", {
  state: () => {
    return {
      storeId: "",
      name:"",
      // name: '',
      package: null,
      // storeList: [],
      expireDate:null,
      isJobchainV2 : false,
      // เวลาเริ่ม session presence รอบปัจจุบัน (ISO) — ต่อเน็ตใหม่หลังโดนลบ node = รอบใหม่
      presenceStartedAt: null as string | null,
    };
  },
  getters: {
    getExpiredDate(state) {       
      if(!state.expireDate){
        return null
      }

      let addDayToExpireDate = dayjs(state.expireDate).add(31, 'hours'); //  1 day and 7 hours     
      return addDayToExpireDate
    },
    getIsJobchainV2(state){
      return state.isJobchainV2
    },
    getPackage(state) {       
      if(!state.package){
        return null
      }
      return state.package
    },
    displayExpiredDate(state){
      if(!state.expireDate){
        return null
      }

      let addDayToExpireDate = dayjs(state.expireDate); //  1 day and 7 hours
      return addDayToExpireDate.format("DD/MM/YYYY")
    }
  },
  actions: {
    async init() {
      const authStore = useAuthStore();
      const uid = authStore.uid;

      // get storeId by uid
      // memoize ร่วมกับ run-mode migration plugin ที่ resolve storeId ไปแล้วตอน boot
      // uid เดิม = ใช้ผลเดิม ไม่ยิง /users/{uid} ซ้ำ
      let res = await fetchUserRecord(uid);

      // Resolve which shop this boot targets. The override must win BEFORE
      // registerModule() fans the storeId out to axios (the Bearer header) +
      // Firestore/RTDB paths.
      //   1. DEV store override — dev-only, import.meta.dev-gated + tree-shaken
      //      out of prod, so getDevStoreOverride() is a static `null` there.
      //      See utils/devStoreOverride.ts.
      //   2. the storeId the backend derived from the logged-in uid.
      const realStoreId = res.data[0];
      setDevRealStore(realStoreId);
      this.storeId = getDevStoreOverride() || realStoreId;
      // เผื่อ plugin resolve ไม่ทัน/ไม่สำเร็จ (เช่น login เสร็จแล้วเข้าหน้าแอปเลย)
      // ป้อน storeId กลับเข้า resolver แล้ว migrate ให้อีกรอบ — migrateRunMode()
      // idempotent อยู่แล้ว เรียกซ้ำไม่มีผลข้างเคียง แต่ต้องเสร็จก่อน
      // trackCashierOnline() ข้างล่างที่อ่าน getMode() ไปเขียน firebase
      setResolvedStoreId(this.storeId);
      migrateRunMode(this.storeId);
      // เผื่อ plugin ตอน boot ไม่รู้ร้าน — ensure ID ของร้านนี้แล้วชี้ authStore ไปที่ ID นี้
      // (computerUuid ใน auth เป็น ref ของ key เดิม set ค่าแล้ว key เดิมก็ตามไปด้วย)
      const identity = ensureComputerIdentity(this.storeId);
      if (identity && authStore.computerUuid !== identity.id) {
        authStore.computerUuid = identity.id;
      }

      // ร้านที่แอปใช้ต้องตรงกับ custom claim storeId ใน id token เสมอ ทุกโดเมน
      //
      // RTDB rules ตัดสินสิทธิ์ "เขียน" stores/$storeId/jobchain จาก claim ถ้าไม่ตรง
      // ออเดอร์ทุกใบจะโดน permission_denied (rtdbGuard alert + เด้งออกกลางกะ)
      // จึงกันตั้งแต่ตอน boot ก่อน registerModule() ตอนที่ยังไม่มีอะไรถูกเขียน
      //
      // dev store override ตั้งใจให้ไม่ตรง — สวมร้านอื่นดูได้ แต่เขียน jobchain
      // ของร้านนั้นไม่ได้ ซึ่งถูกแล้ว จึงข้ามการตรวจ
      if (!getDevStoreOverride() && !(await checkStoreClaim(this.storeId, uid))) {
        // logout กำลังพาไป /login หยุด bootstrap ไว้ตรงนี้ ไม่ให้ขั้นถัดไปรันกับร้านที่ไม่มีสิทธิ์
        return BOOTSTRAP_STOP;
      }

      this.registerModule(this.storeId);
      // When impersonating, load the impersonated shop's metadata (name/expiry);
      // otherwise keep the original `res.data` call untouched.
      this.getStoreData(this.storeId === realStoreId ? res.data : [this.storeId]);
      this.getStoreLastUpdate(this.storeId)

      // dev สวมร้านอื่น: ไม่ประกาศตัวใน stores/{id}/online ของร้านเขา
      if (!isDevReadOnly()) this.trackCashierOnline();
    },
    async fetchBetaWhiteLists() {            

      
      const currentUrl = useRequestURL()


      // check is localhost
      if(currentUrl.hostname == "localhost"){
        return
      }

      
      let firestore = useFirebaseFirestore()    
      let collectionRef = firestore.collectionBetaWhiteList()   
      
      // โดเมน beta มาจาก config (ปกติ app2.xtable.co ตอนทดสอบเป็น URL preview channel)
      const onBetaSite = isBetaSite()
      




      const docSnap = await getDoc(collectionRef);         
      if (docSnap.exists() && !onBetaSite) {
          const authStore = useAuthStore();
          const uid = authStore.uid

          // ส่งไป app2 เฉย ๆ ไม่มี token — app2 ใช้ session ของตัวเองที่ได้ตอน login ครั้งแรก
          // (token ขอเฉพาะตอน login ที่หน้า /login ดู utils/authHandoff.ts)
          // replace ไม่ทิ้งหน้านี้ไว้ใน history กด back แล้วจะไม่วนกลับมา redirect อีก
          writeHandoffCookie({ uid, storeId: this.storeId })
          window.location.replace(betaOrigin())
          // หยุด bootstrap queue ระหว่างรอ redirect (ห้าม throw เพราะ Sentry จะจับเป็น "No error message")
          return BOOTSTRAP_STOP
          
      }
      

  },    
    registerModule(storeId) {
      // set firebase path
      const fb = useFirebase();
      fb.setStoreId(storeId);

      const fbFirestore = useFirebaseFirestore();
      fbFirestore.setStoreId(storeId);

      // set dataservice receipt path
      // dataservice.receipts.setStoreId(storeId)
      // dataservice.jobs.setStoreId(storeId)
      // dataservice.products.setStoreId(storeId)
      // dataservice.inventories.setStoreId(storeId)
      // dataservice.store.setStoreId(storeId)
      // dataservice.users.setStoreId(storeId)
      // dataservice.settings.setStoreId(storeId)
      // dataservice.employees.setStoreId(storeId)

      for (const key in dataservice) {
        dataservice[key].setStoreId(storeId);
      }
    },
    async getStoreData(storeId) {
      try {    
        const res = await dataservice.store.getById(storeId); 
        let expireDate = res?.data?.expireDate ;     
        //let expireDate = res?.data?.expireAt ;     
         
        this.$patch({
          ...res.data,         
        });


        this.expireDate = expireDate
        console.log("store data", this.$state)
      } catch (error) {
        console.log(error);
      }
    },

    async getStoreLastUpdate(storeId:string) {
      try {    
        const res = await new DataService(`/stores/${storeId}/lastUpdate`).getAll()
        console.log("Lasted Update... ", res.data)        
      } catch (error) {
        console.log(error);
      }
    },

    async updateStoreName(name: string) {
      const storeId = this.storeId;
      if (!storeId) {
        throw new Error("Store id not found");
      }

      const payload = {
        storeId,
        name,
      };

      await new DataService(`/stores/${storeId}`).put(payload);
      this.name = name;
    },

    trackCashierOnline() {
      // กันเรียกซ้ำ (เช่น shop.init รันใหม่) ไม่ให้มี timer/listener ซ้อนกัน
      this.stopPresenceListeners();

      const authStore = useAuthStore();
      const computerUUid = authStore.computerUuid;
      const onlineRef = useFirebase().onlineRef(computerUUid);
      presenceRef = onlineRef;

      this.presenceStartedAt = dayjs().format();

      // heartbeat ต้องบอกได้ด้วยว่าเครื่องนี้รันเวอร์ชันไหน และถือ jobchain ยาวแค่ไหน
      // เวลาเจอเคส "แอบเป็น main cashier ซ้ำ" จะได้ไล่ได้ว่าเครื่องไหน build เก่า
      // และเครื่องไหน sync ไม่ครบ (jobCount ไม่เท่ากันทั้งที่เป็นร้านเดียวกัน)
      // runMode อ่านสดทุกรอบ — เปลี่ยนโหมดกลาง session แล้ว presence ต้องตามทัน
      const buildPayload = () => {
        const identity = readComputerIdentity(this.storeId);
        return {
          runMode: useRunningMode().getMode(),
          timestamp: dayjs().format(),
          lastUpdate: serverTimestamp(),
          startedAt: this.presenceStartedAt,
          // ออนไลน์รอบนี้มานานเท่าไร (นับจาก startedAt) อ่านง่ายกว่าเทียบ timestamp เอง
          onlineFor: formatDuration(Date.now() - dayjs(this.presenceStartedAt).valueOf()),
          // pageLoadedAt / pageAgeFor / userAgent / isMobile ... ดู utils/presenceInfo.ts
          ...getPresenceDeviceInfo(),
          computerUUid,
          computerName: identity?.name || null,
          computerCreatedAt: identity?.createdAt || null,
          computerCreatedAtEstimated: identity?.createdAtEstimated ?? null,
          appVersion: packageData.version,
          jobCount: useJobsStore().jobs.length,
          // liveSync* = สถานะการเขียนข้อมูลให้มือถือ (ดู utils/liveSyncStatus.ts)
          ...getLiveSyncStatus(),
        };
      };

      presenceBuildPayload = buildPayload;

      // ทุกครั้งที่ต่อ RTDB ได้: ตั้ง onDisconnect ใหม่ (server ลบ trigger ทิ้งหลังทำงานไปแล้ว)
      // แล้วเขียน node ใหม่ ปิด tab / เน็ตหลุด → server ลบ node ให้เอง ไม่ค้างเป็นผี
      let firstConnect = true;
      presenceUnsubConnected = onValue(
        ref(getDatabase(), ".info/connected"),
        (snap) => {
          if (snap.val() !== true) return;
          // ต่อใหม่หลังหลุด node เดิมโดนลบไปแล้ว นับเป็น session ใหม่
          if (!firstConnect) this.presenceStartedAt = dayjs().format();
          firstConnect = false;

          onDisconnect(onlineRef)
            .remove()
            .catch((e) => console.warn("[presence] ตั้ง onDisconnect ไม่สำเร็จ", e));
          set(onlineRef, buildPayload()).catch((e) =>
            console.warn("[presence] เขียน online ไม่สำเร็จ", e)
          );
        }
      );

      // update every 1 min
      presenceTimer = setInterval(() => {
        set(onlineRef, buildPayload()).catch(() => {});
      }, 1000 * 60);
    },

    /**
     * ลบ stores/{storeId}/online ทั้ง node (ทุกเครื่อง) — Main cashier เรียกตอนปิดร้าน
     *
     * node ของเครื่องที่ค้าง (onDisconnect ไม่ทำงาน, bundle เก่า, เครื่องที่ไม่ได้ใช้แล้ว)
     * จะไม่หายเอง ล้างทั้งหมดทุกรอบปิดร้าน เครื่องที่ยังเปิดอยู่จะเขียนตัวเองกลับมา
     * ใน heartbeat รอบถัดไป (ไม่เกิน 1 นาที) ส่วนเครื่องนี้เขียนกลับทันที
     *
     * ห้าม throw / ห้ามค้าง: ลบไม่ได้แค่ node เก่าค้างต่อ ไม่ใช่เหตุให้ปิดร้านไม่ผ่าน
     */
    async clearOnlineNodes() {
      const storeId = this.storeId;
      if (!storeId) return;

      try {
        await Promise.race([
          remove(ref(getDatabase(), `stores/${storeId}/online`)),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("timeout")), CLEAR_ONLINE_TIMEOUT_MS)
          ),
        ]);
      } catch (e) {
        console.warn("[presence] ล้าง node online ไม่สำเร็จ", e);
      }

      // เครื่องนี้ยังออนไลน์อยู่ — เขียนตัวเองกลับ ไม่ต้องรอ heartbeat นาทีถัดไป
      // (onDisconnect ที่ตั้งไว้ยังอยู่ ไม่ต้องตั้งใหม่)
      if (presenceRef && presenceBuildPayload) {
        set(presenceRef, presenceBuildPayload()).catch((e) =>
          console.warn("[presence] เขียน online ไม่สำเร็จ", e)
        );
      }
    },

    stopPresenceListeners() {
      if (presenceTimer) clearInterval(presenceTimer);
      presenceTimer = null;
      presenceUnsubConnected?.();
      presenceUnsubConnected = null;
    },

    /** หยุด heartbeat แล้วลบ node online ของเครื่องนี้ — ใช้ตอน logout ก่อน signOut */
    async stopCashierOnline() {
      this.stopPresenceListeners();
      const target = presenceRef;
      presenceRef = null;
      presenceBuildPayload = null;
      this.presenceStartedAt = null;
      if (!target) return;

      // offline อยู่ remove() จะไม่ resolve จนกว่าจะต่อเน็ตได้ — ไม่ให้ logout ค้าง
      // ถ้าลบไม่ทัน onDisconnect ที่ตั้งไว้ก็ลบให้ตอน tab ปิดอยู่ดี
      try {
        await Promise.race([
          remove(target),
          new Promise((resolve) => setTimeout(resolve, 2000)),
        ]);
      } catch (e) {
        console.warn("[presence] ลบ node online ไม่สำเร็จ", e);
      }
    },
  },
});

/**
 * ร้านที่แอปใช้ (storeId จาก GET /users/{uid}) ตรงกับ custom claim storeId ไหม
 *
 * ไม่ตรง = alert บอกรหัส + ข้อมูลที่ support ต้องใช้ตอบลูกค้า, ส่ง Sentry ข้อมูลชุดเดียวกัน
 * (ค้นด้วย tag authCheck / storeId / uid ได้) แล้ว logout เฉพาะโดเมนนี้
 * support ซ่อมด้วย POST /users/{uid}/claims/refresh แล้วให้ร้าน login ใหม่
 *
 * @returns true = ใช้งานต่อได้
 */
async function checkStoreClaim(storeId: string, uid: string): Promise<boolean> {
  const user = getAuth().currentUser;

  let claim: string | null = null;
  try {
    // อ่านจาก token ที่ authStore.init() เพิ่ง force refresh ไว้ ไม่อ่าน authStore.storeIdClaim
    // เพราะ listener onIdTokenChanged ตั้งค่านั้นเป็น null ชั่วคราวตอน token หมุน
    claim = user ? ((await user.getIdTokenResult()).claims.storeId as string) || null : null;
  } catch (error) {
    console.error("[shop] อ่าน custom claim ไม่สำเร็จ", error);
  }

  if (claim && claim === storeId) return true;

  const code = claim ? "CLAIM_MISMATCH" : "CLAIM_MISSING";
  const host = window.location.hostname;
  const email = user?.email || "-";

  console.error("[shop] ร้านไม่ตรงกับ custom claim", { code, storeId, claim, uid, email, host });
  Sentry.captureMessage("[auth] store claim check failed", {
    level: "error",
    tags: { authCheck: code, storeId: storeId || "none", claimStoreId: claim || "none", uid },
    extra: { email, host },
  });

  const reason = claim
    ? "สิทธิ์ร้านของบัญชีนี้ไม่ตรงกับร้านที่ใช้งาน"
    : "บัญชีนี้ยังไม่ได้รับสิทธิ์ร้าน";
  alert(
    `เข้าใช้งานไม่ได้: ${reason} (รหัส ${code})\n\n` +
      `ร้านของบัญชี: ${storeId || "-"}\n` +
      `สิทธิ์ในระบบ: ${claim || "ไม่มี"}\n` +
      `บัญชี: ${email} (uid ${uid})\n` +
      `เครื่อง: ${host}\n\n` +
      `กรุณาแจ้งรหัสนี้กับทีม support`
  );

  useAuthStore().logout();
  return false;
}
